import React, { useCallback, useEffect, useState } from "react";
import { DragDropContext, Draggable, DraggableLocation, Droppable, OnDragEndResponder } from "react-beautiful-dnd";
import InfiniteScroll from "react-infinite-scroll-component";

import { Box, CircularProgress, Stack } from "@mui/material";
import { DealCreate } from "components/modals";
import { useCannotMoveToStageHook, useDidUpdate, useModals, useNavigate, usePrompt } from "hooks";
import { pickBy } from "lodash";
import { useDealActions, useDealQueriesSelect, usePickUserPermissions, useUserRoleSelector } from "store";
import { CreateDealStageData, DealStage, GetDealStagesData, UpdateDealStageData } from "store/types";

import { RoleName } from "../../../../constants";
import { CreatePipeline } from "../../../My-deals";
import { PipelineColumn } from "./PipelineColumn";

interface HandleDragColumnData {
  sourceIndex: number;
  targetIndex: number;
}

interface HandleDragCardData {
  destination: DraggableLocation;
  draggableId: string;
}

export const PipelineView: React.FC = () => {
  const [isCreate, setIsCreate] = useState<boolean>(false);
  const [data, setData] = useState<DealStage[]>([]);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const dealQueries = useDealQueriesSelect();
  const role = useUserRoleSelector();
  const myDeals = usePickUserPermissions("deals");
  const openPrompt = usePrompt();
  const navigate = useNavigate();
  const isViewerPermissionDeals = myDeals && myDeals === "viewer";
  const { deal_title, order_by, filters } = dealQueries || {};

  const {
    getDealStagesAction,
    deleteDealStageAction,
    updateDealStageAction,
    createDealStageAction,
    reorderDealStagesAction,
    updateDealStageItemAction
  } = useDealActions();
  const { deleteModal, createModal, errorModal } = useModals();
  const { showCannotMoveToStageModal } = useCannotMoveToStageHook();

  const createDeal = async (stageId?: number) => {
    const data = await openPrompt({ Component: DealCreate, props: { stageId } });

    if (!data) return;

    navigate({ route: "deal", params: { deal_id: data.id } });
  };

  const getDealStages = useCallback(
    async (data: GetDealStagesData<true> & { turnOnLoading?: boolean }) => {
      try {
        if (data.turnOnLoading) {
          setLoading(true);
        }
        const { data: stages, last_page } = await getDealStagesAction({ ...data, freemium_dashboard: true });
        if (data.page! >= last_page) {
          setHasMore(false);
        }

        setPage((prevState) => prevState + 1);
        setData((prevState) => {
          const updatedStages: DealStage[] = stages.map((newStage) => {
            const stage = prevState.find((item) => item.id === newStage.id);
            if (stage) {
              return {
                ...stage,
                deals: [...stage.deals, ...newStage.deals]
              };
            }
            return newStage;
          });

          return updatedStages;
        });
      } finally {
        if (data.turnOnLoading) {
          setLoading(false);
        }
      }
    },
    [setPage, hasMore]
  );

  const resetParams = useCallback(() => {
    setData([]);
    setPage(1);
    setLoading(false);
    setHasMore(true);
  }, []);

  // Initial API call
  useEffect(() => {
    const filtersPayload = pickBy(filters, (value) => value.length > 0);
    getDealStages({
      filters: filtersPayload,
      deal_title,
      with_deals: true,
      order_by,
      page: 1,
      turnOnLoading: true
    });
  }, []);

  // Listen queries, reset params and call api with default params
  useDidUpdate(() => {
    const filtersPayload = pickBy(filters, (value) => value.length > 0);
    resetParams();
    getDealStages({
      filters: filtersPayload,
      with_deals: true,
      deal_title,
      order_by,
      page: 1
    });
  }, [dealQueries]);

  const deleteColumn = useCallback(
    ({ title, id }: DealStage) => {
      deleteModal({
        element: "DELETE",
        message: `Are you sure you want to delete the column: ${title}?`,
        action: async () => {
          await deleteDealStageAction(id);
          setData((prevState) => prevState.filter((item) => item.id !== id));
        }
      });
    },
    [setData, deleteModal]
  );

  const updateColumn = useCallback(
    async (dataToSend: UpdateDealStageData) => {
      const stage = await updateDealStageAction(dataToSend);
      if (!data) return;
      setData(data.map((item) => (item.id === stage.id ? stage : item)));
      return stage;
    },
    [data]
  );

  const renameColumn = useCallback(
    (stage: DealStage) => {
      createModal({
        title: "Rename column",
        action: async (title: string) => updateColumn({ id: stage.id, title }),
        submitButtonTitle: "Ok",
        defaultValue: stage.title
      });
    },
    [createModal, updateColumn]
  );

  const handleCreateColumn = useCallback(
    async (dataToSend: CreateDealStageData): Promise<void> => {
      const pipeline = await createDealStageAction(dataToSend);
      if (!data) return;
      setData([...data, pipeline]);
    },
    [createModal, data]
  );

  const handleDragColumn = useCallback(
    async ({ sourceIndex, targetIndex }: HandleDragColumnData) => {
      if (!data) return;
      const result = Array.from(data);
      const [removed] = result.splice(sourceIndex, 1);
      result.splice(targetIndex, 0, removed);

      setData(result.map((item, index) => ({ ...item, order: index })));

      try {
        await reorderDealStagesAction({ order: result.map(({ id }, index) => ({ id, position: index })) });
      } catch (_unused) {
        errorModal({ msg: "Something went wrong" });
        setData(data);
      }
    },
    [data]
  );

  const handleDragCard = useCallback(
    async ({ destination, draggableId }: HandleDragCardData) => {
      if (!data) return;

      const [sourceStage, dealId] = draggableId.split("-").map(Number);
      const [, targetStage] = destination.droppableId.split("stage-").map(Number);

      if (sourceStage === targetStage) return;

      const sourceStageData = data.find((stage) => stage.id === sourceStage);
      if (!sourceStageData) return;
      const stageItemToMove = sourceStageData.deals.find((deal) => deal.id === dealId);
      if (!stageItemToMove) return;

      const updatedData = data.map((stage) => {
        if (stage.id === sourceStage) {
          return {
            ...stage,
            deals: stage.deals.filter((deal) => deal.id !== dealId),
            deals_count: stage.deals_count - 1
          };
        }
        if (stage.id === targetStage) {
          return { ...stage, deals: [stageItemToMove, ...stage.deals], deals_count: stage.deals_count + 1 };
        }
        return stage;
      });

      setData(updatedData);

      const revertMove = () => {
        setData((prevData) =>
          prevData.map((stage) => {
            if (stage.id === sourceStage) {
              return { ...stage, deals: [...stage.deals, stageItemToMove], deals_count: stage.deals_count + 1 };
            }
            if (stage.id === targetStage) {
              return { ...stage, deals: stage.deals.filter((deal) => deal.id !== dealId), deals_count: Math.max(0, stage.deals_count - 1) };
            }
            return stage;
          })
        );
      };

      try {
        await updateDealStageItemAction({ dealId, deal_stage_id: targetStage });
      } catch (error: unknown) {
        const err = error as {
          status?: number;
          errors?: { code?: string; missing_fields?: Array<{ id: number; name: string }>; target_status?: string; target_stage_id?: number };
          response?: { status: number; data?: { errors?: { code?: string; missing_fields?: Array<{ id: number; name: string }>; target_status?: string; target_stage_id?: number } } };
        };
        const status = err.status ?? err.response?.status;
        const errors = err.errors ?? err.response?.data?.errors;
        if (status === 422 && errors?.code === "status_transition_validation_failed" && errors?.missing_fields?.length) {
          const targetStageName = data.find((s) => s.id === targetStage)?.title ?? "this stage";
          revertMove();
          showCannotMoveToStageModal({
            dealId,
            targetStageName,
            targetStageId: targetStage,
            missingFields: errors.missing_fields
          });
        } else {
          errorModal({ msg: "Something went wrong" });
          revertMove();
        }
      }
    },
    [data, updateDealStageItemAction, errorModal, showCannotMoveToStageModal]
  );

  const handleDrag: OnDragEndResponder = useCallback(
    async ({ destination, source, type, draggableId }) => {
      if (typeof destination?.index !== "number") return;

      if (type === "PIPELINE") {
        handleDragColumn({ sourceIndex: source.index, targetIndex: destination.index });
      }

      if (type === "STAGE") {
        handleDragCard({ destination, draggableId });
      }
    },
    [handleDragColumn, handleDragCard]
  );
  return (
    <>
      <Box
        sx={{
          height: "100%",
          overflow: "hidden",
          width: "100%",
          flexGrow: 1,
          borderRadius: "8px",
          border: "1px solid #dbe8f5",
          bgcolor: "#f6f8fc",
          p: "10px"
        }}
      >
        <InfiniteScroll
          height="100%"
          next={() => {
            const filtersPayload = pickBy(filters, (value) => value.length > 0);
            getDealStages({
              filters: filtersPayload,
              deal_title,
              with_deals: true,
              order_by,
              page
            });
          }}
          hasMore={hasMore}
          dataLength={page * 50}
          loader={
            <Stack height="100%" justifyContent="center" alignItems="center">
              <CircularProgress size={150} />
            </Stack>
          }
        >
          {!loading && Boolean(data.length) && (
            <DragDropContext onDragEnd={handleDrag}>
              <Droppable droppableId="pipeline" direction="horizontal" type="PIPELINE">
                {(provided) => (
                  <Stack
                    direction="row"
                    gap="12px"
                    paddingBottom="10px"
                    alignItems="flex-start"
                    minHeight="100%"
                    flexGrow="1"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {data &&
                      (() => {
                        // Filter out columns with status_id === 100 to get visible columns
                        const visibleColumns = data.filter((item) => item.status_id !== 100);
                        // Check if all columns are empty (no deals in any column)
                        const allColumnsEmpty = visibleColumns.every((stage) => !stage.deals.length);

                        return data.map((item, index) => {
                          if (item.status_id === 100) return null;
                          // Find the index in the visible columns array
                          return (
                            <Draggable
                              isDragDisabled={role?.name === RoleName.TeamViewer || isViewerPermissionDeals}
                              key={item.id}
                              draggableId={item.id.toString()}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <PipelineColumn
                                  onDeleteColumn={deleteColumn}
                                  onRenameColumn={renameColumn}
                                  onCreateDeal={(stageId) => createDeal(stageId)}
                                  updateColumn={updateColumn}
                                  stage={item}
                                  key={item.id}
                                  draggableProvided={provided}
                                  draggableSnapshot={snapshot}
                                  isFirstColumn={!index}
                                  allColumnsEmpty={allColumnsEmpty}
                                />
                              )}
                            </Draggable>
                          );
                        });
                      })()}
                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </InfiniteScroll>
      </Box>
      <CreatePipeline isOpen={isCreate} onClose={() => setIsCreate(false)} onSubmit={handleCreateColumn} />
    </>
  );
};
